import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router-dom";
import { APIError, AssetAllocationRate, FormFieldConfig } from "../helpers/shared-interfaces";
import { generateDisplayErrorMessage, handleValidationErrors, mapAPIErrorToFormError, request, responseHandler , showTotalPagination } from "../helpers/utilities";
import * as Sentry from "@sentry/browser";
import { Button, Col, FormInstance, Row, Table, message } from "antd";
import Card from "../components/Card";
import EntitledSection from "../components/EntitledSection";
import entitlements from "../config/entitlements";
import { PlusOutlined } from "@ant-design/icons";
import { ColumnType } from "antd/lib/table";
import { CREATE_ASSET_ALLOCATION_RATE, GET_ASSET_ALLOCATION_RATES } from "../gql/economicIndicators";
import BaseForm from "../components/Form/BaseForm";
import { AxiosResponse } from "axios";
import ModalCreator from "../components/ModalCreator";
import { PageToolbar } from "../components/PageToolbar";

interface State {
  isLoading: boolean;
  assetAllocationRates: AssetAllocationRate[];
  page: number;
  pageSize: number;
  id?: number;
  showNewAssetAllocationRateModal: boolean;
  isCreatingAssetAllocationRate: boolean;
}

export function getAssetAllocationRatesColumn<T extends AssetAllocationRate>(
  assetAllocationRates: AssetAllocationRate[],
): ColumnType<T>[] {
  return [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render(text: string): JSX.Element {
        return <span>{text}</span>;
      },
    },
  ];
}

export const NewAssetAllocationItemUI = (props: {
  reference: React.RefObject<FormInstance<any>>;
}): JSX.Element => {
  const fields: FormFieldConfig[] = [
    {
      id: "name",
      name: "name",
      rule: "string",
      inputType: "string",
      required: true,
      label: "Name",
      placeholder: "Enter Asset Allocation Rate Name",
      colConfig: {
        span: 24,
      },
    },
  ];

  return (
    <BaseForm
      layout="vertical"
      reference={props.reference}
      fieldsToHide={[]}
      fields={fields}
    />
  );
};

export default class AssetAllocationRates extends React.Component<
  RouteComponentProps,
  State
> {
  reference: RefObject<FormInstance>;
  constructor(props: Readonly<RouteComponentProps>) {
    super(props);
    this.state = {
      isLoading: true,
      assetAllocationRates: [],
      page: 1,
      pageSize: 25,
      showNewAssetAllocationRateModal: false,
      isCreatingAssetAllocationRate: false
    };
    this.reference = React.createRef<FormInstance>();
    this.createAssetAllocationRate = this.createAssetAllocationRate.bind(this);
    this.nextPage = this.nextPage.bind(this);
    this.toggleCreateAssetAllocationRateModal = this.toggleCreateAssetAllocationRateModal.bind(this);
  }

  async componentDidMount(): Promise<void> {
    this.getassetAllocationRates();
  }

  redirectToViewAssetAllocationRatePage(): void {
    this.props.history.push(`/asset-allocation-rates/${this.state.id}`);
  }

  async createAssetAllocationRate(): Promise<void> {
    interface Response {
      assetAllocationRate: AssetAllocationRate;
      errors: APIError[];
    }

    if (!this.reference.current) return;
    let data;
    try {
      data = await this.reference.current.validateFields(["name"]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    try {
      const response: AxiosResponse<Response> = await request({
        query: CREATE_ASSET_ALLOCATION_RATE,
        variables: data,
      });

      const { err, handled, mutation } = responseHandler<Response>(
        response,
        "createAssetAllocationRate",
      );

      if (handled) {
        message.error("An Error Occurred. Your request couldn't be processed.");
        return;
      } else if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.reference.current.setFields(err.map(mapAPIErrorToFormError));
      } else {
        this.setState({ id: mutation?.assetAllocationRate?.id }, () => {
          message.success("Asset allocation rate created sucessfully");
          this.redirectToViewAssetAllocationRatePage();
        });
      }
    } catch (error) {
      message.error(`Failed to create asset allocation rate - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: true });
      this.toggleCreateAssetAllocationRateModal();
    }
  }

  async nextPage(page: number, pageSize: number): Promise<void> {
    this.setState(
      {
        isLoading: true,
        page,
        pageSize,
      },
      async () => {
        await this.getassetAllocationRates();
      },
    );
  }

  getassetAllocationRates = async (): Promise<void> => {
    const { page } = this.state;
    this.setState({ isLoading: true });
    try {
      const response: any = await request({
        query: GET_ASSET_ALLOCATION_RATES,
        variables: { page },
      });
      const { data } = response;
      this.setState({
        assetAllocationRates: data.data?.assetAllocationRates || [],
      });
    } catch (error) {
      message.error(`Failed To Load Asset Allocation Rates - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  toggleCreateAssetAllocationRateModal(): void {
    this.setState((prevState) => ({
      showNewAssetAllocationRateModal: !prevState.showNewAssetAllocationRateModal
    }));
  }

  render(): JSX.Element | null {
    const { isLoading, assetAllocationRates, page, pageSize, showNewAssetAllocationRateModal } = this.state;

    const resultsWithKeys: AssetAllocationRate[] = assetAllocationRates.map(
      (result: AssetAllocationRate): AssetAllocationRate => {
        return {
          ...result,
          id: result.id,
        };
      },
    );

    return (
      <Card>
        <div className="asset-allocation-rates-container">
          <PageToolbar
            totalCount={assetAllocationRates.length}
            currentDataCount={assetAllocationRates.length}
            page={page}
            pageSize={pageSize}
            modelName="asset allocation rates"
            primaryAction={
              <EntitledSection
                entitlement={entitlements.EconomicIndicators.create}
              >
                <Button
                  className="base-button"
                  size="large"
                  data-testid="create-asset-allocation-rate-button"
                  onClick={this.toggleCreateAssetAllocationRateModal}
                >
                  <PlusOutlined />
                  Create Asset Allocation Item
                </Button>
              </EntitledSection>
            }
          />

          <Row>
            <Col span={24}>
              <Table
                loading={isLoading}
                scroll={{ x: 400 }}
                columns={getAssetAllocationRatesColumn(
                  assetAllocationRates,
                )}
                dataSource={resultsWithKeys}
                onRow={(record) => ({
                  onClick: () => {
                    this.props.history.push({
                      pathname: `/asset-allocation-rates/${record.id}`,
                      state: { assetAllocationRates },
                    });
                  },
                  style: { cursor: 'pointer' }

                })}
                pagination={{
                  current: page,
                  defaultPageSize: 25,
                  total: assetAllocationRates.length,
                  onChange: this.nextPage,
                  showTotal: showTotalPagination,
                }}
              />
            </Col>
          </Row>
          {showNewAssetAllocationRateModal &&
            ModalCreator({
              title: "Create Asset Allocation Rate",
              okType: "primary",
              okText: "Create",
              showModal: showNewAssetAllocationRateModal,
              onOkay: this.createAssetAllocationRate,
              toggleModal: this.toggleCreateAssetAllocationRateModal,
              inscription: NewAssetAllocationItemUI({
                reference: this.reference,
              }),
            })}
        </div>
      </Card>
    );
  }
}
